import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Smile,
  Frown,
  Moon,
  Sun,
  Zap,
  Feather,
  User,
  Users,
  Sparkles,
} from "lucide-react";
import { getJournalQuestion } from "../../api/api"; // Import the API utility
import { useTier } from "../../hooks/useTier";
import { useUpsell } from "../paywall/UpsellProvider";
import { useLanguage } from "../../i18n/LanguageContext";

const EntryForm = ({ onClose, onSave, editEntry = null, isSaving = false, initialDate = null }) => {
  const [entry, setEntry] = useState({
    title: "",
    content: "",
    date: initialDate || new Date().toISOString().split("T")[0],
    sentiment: 3,
    sleep: 3,
    stress: 3,
    socialEngagement: 3,
  });
  const [aiJournalingActive, setAiJournalingActive] = useState(false);
  const [aiQuestion, setAiQuestion] = useState("");
  const [loadingAiQuestion, setLoadingAiQuestion] = useState(false);
  const [aiQuestionsUsed, setAiQuestionsUsed] = useState(0);
  const [reflectionLocked, setReflectionLocked] = useState(false);
  const [formError, setFormError] = useState("");
  const debounceTimeoutRef = useRef(null);
  const aiQuestionsUsedRef = useRef(0);
  const { isPremium } = useTier();
  const upsell = useUpsell();
  const { t } = useLanguage();
  const questionLimit = 3;
  const entryId = editEntry?.entry_id || editEntry?.id || null;

  useEffect(() => {
    if (editEntry) {
      setEntry({
        ...editEntry,
        date: editEntry.date ? editEntry.date.split("T")[0] : "",
        sentiment:
          editEntry.sentiment_level !== undefined &&
          editEntry.sentiment_level !== null
            ? editEntry.sentiment_level
            : 3,
        sleep:
          editEntry.sleep_quality !== undefined &&
          editEntry.sleep_quality !== null
            ? editEntry.sleep_quality
            : 3,
        stress:
          editEntry.stress_level !== undefined &&
          editEntry.stress_level !== null
            ? editEntry.stress_level
            : 3,
        socialEngagement:
          editEntry.social_engagement !== undefined &&
          editEntry.social_engagement !== null
            ? editEntry.social_engagement
            : 3,
      });
    } else {
      setEntry({
        title: "",
        content: "",
        date: initialDate || new Date().toISOString().split("T")[0],
        sentiment: 3,
        sleep: 3,
        stress: 3,
        socialEngagement: 3,
      });
    }
    const initialQuestionsUsed = editEntry?.ai_questions_used || 0;
    aiQuestionsUsedRef.current = initialQuestionsUsed;
    setAiQuestionsUsed(initialQuestionsUsed);
    setReflectionLocked(false);
    setFormError("");
  }, [editEntry, initialDate]);

  const fetchAiQuestion = useCallback(async (currentContent) => {
    if (!isPremium && aiQuestionsUsedRef.current >= questionLimit) {
      setReflectionLocked(true);
      return;
    }
    setLoadingAiQuestion(true);
    try {
      const data = await getJournalQuestion(currentContent, entryId);
      setAiQuestion(data.question);
      if (!isPremium) {
        const used = data.ai_questions_used ?? aiQuestionsUsedRef.current + 1;
        aiQuestionsUsedRef.current = used;
        setAiQuestionsUsed(used);
        setReflectionLocked(used >= questionLimit);
      }
    } catch (error) {
      console.error("Error fetching AI question:", error);
      if (error.message.includes("reflection_quota_exceeded")) {
        aiQuestionsUsedRef.current = questionLimit;
        setAiQuestionsUsed(questionLimit);
        setReflectionLocked(true);
        setAiQuestion("");
        return;
      }
      setAiQuestion(t("entry.aiFailed"));
    } finally {
      setLoadingAiQuestion(false);
    }
  }, [entryId, isPremium, t]);

  useEffect(() => {
    if (aiJournalingActive && reflectionLocked) {
      return;
    }
    if (aiJournalingActive) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        fetchAiQuestion(entry.content);
      }, 1000); // Debounce for 1 second
    } else {
      setAiQuestion(""); // Clear question when AI journaling is inactive
    }
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [aiJournalingActive, entry.content, fetchAiQuestion, reflectionLocked]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    const newValue = type === "range" ? parseInt(value, 10) : value;

    setEntry((prev) => ({ ...prev, [name]: newValue }));
    if (formError) {
      setFormError("");
    }
  };

  const handleToggleAiJournaling = () => {
    if (!isPremium && reflectionLocked) {
      upsell?.open(t("upsell.reflectionLimitReason"));
      return;
    }
    setAiJournalingActive((prev) => !prev);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!entry.title || !entry.content || !entry.date) {
      setFormError(t("entry.required"));
      return;
    }
    onSave(entry);
  };

  const renderSlider = (name, label, min, max, minIcon, maxIcon, step = 1) => (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700">
        {label}: {entry[name]}
      </label>
      <div className="flex items-center space-x-2 mt-1">
        {minIcon}
        <input
          type="range"
          id={name}
          name={name}
          min={min}
          max={max}
          step={step}
          value={entry[name]}
          onChange={handleChange}
          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer range-lg"
        />
        {maxIcon}
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-grow pt-4">
      <div className="flex-grow overflow-hidden pr-2 space-y-4">
        {formError && (
          <p className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {formError}
          </p>
        )}

        <div className="flex gap-3">
          <div className="flex-1">
            <label
              htmlFor="title"
              className="block text-sm font-medium text-gray-700"
            >
              {t("entry.title")}
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={entry.title}
              onChange={handleChange}
              placeholder={t("entry.titlePlaceholder")}
              className="mt-1 input-field"
              required
            />
          </div>

          <div className="w-40 flex-shrink-0">
            <label
              htmlFor="date"
              className="block text-sm font-medium text-gray-700"
            >
              {t("entry.date")}
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={entry.date}
              onChange={handleChange}
              className="mt-1 input-field"
              required
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="content"
            className="block text-sm font-medium text-gray-700"
          >
            {t("entry.content")}
          </label>
          <div className="relative mt-1">
            <textarea
              id="content"
              name="content"
              rows="6"
              value={entry.content}
              onChange={handleChange}
              placeholder={t("entry.contentPlaceholder")}
              className="input-field pb-10"
              required
            ></textarea>
            <button
              type="button"
              onClick={handleToggleAiJournaling}
              className={`absolute bottom-3 right-3 px-3 py-1.5 text-xs font-medium rounded-md flex items-center space-x-1.5 transition-colors ${
                aiJournalingActive
                  ? "bg-purple-600 text-white hover:bg-purple-700"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300"
              }`}
            >
              <Sparkles className="h-3.5 w-3.5" />
              <span>
                {aiJournalingActive ? t("entry.aiOn") : t("entry.ai")}
              </span>
            </button>
          </div>
          {aiJournalingActive && (
            <div className="mt-2 text-sm text-gray-600">
              {!isPremium && (
                <div className="mb-2 flex items-center justify-between rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  <span>{t("entry.freeQuestions", { used: aiQuestionsUsed, limit: questionLimit })}</span>
                  {reflectionLocked && (
                    <button
                      type="button"
                      onClick={() => upsell?.open(t("upsell.reflectionLimitReason"))}
                      className="font-medium text-purple-700 hover:text-purple-800"
                    >
                      {t("common.upgrade")}
                    </button>
                  )}
                </div>
              )}
              {loadingAiQuestion ? (
                <span>{t("entry.aiLoading")}</span>
              ) : reflectionLocked ? (
                <p className="italic text-gray-500">
                  {t("entry.freeLimitReached")}
                </p>
              ) : (
                <p className="italic">
                  {aiQuestion || t("entry.startTyping")}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {t("entry.stateTitle")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderSlider(
              "sentiment",
              t("entry.sentiment"),
              1,
              5,
              <Frown className="h-5 w-5 text-gray-500" />,
              <Smile className="h-5 w-5 text-gray-500" />
            )}
            {renderSlider(
              "sleep",
              t("entry.sleepQuality"),
              1,
              5,
              <Moon className="h-5 w-5 text-gray-500" />,
              <Sun className="h-5 w-5 text-gray-500" />
            )}
            {renderSlider(
              "stress",
              t("entry.stressLevel"),
              1,
              5,
              <Feather className="h-5 w-5 text-gray-500" />,
              <Zap className="h-5 w-5 text-gray-500" />
            )}
            {renderSlider(
              "socialEngagement",
              t("entry.socialEngagement"),
              1,
              5,
              <User className="h-5 w-5 text-gray-500" />,
              <Users className="h-5 w-5 text-gray-500" />
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 mt-4 flex-shrink-0 pt-2 border-t border-gray-200">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          {t("common.cancel")}
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={isSaving}
        >
          {isSaving ? t("common.saving") : editEntry ? t("entry.update") : t("entry.save")}
        </button>
      </div>
    </form>
  );
};

export default EntryForm;
